import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import Divider from '@mui/material/Divider';
import {
  notEmptyString,
} from '@populateui/utils';
import Accordion from 'core/components/Accordion';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import TwoChoiceGroup from 'core/components/TwoChoiceGroup';
import Input from 'pages/Dashboard/components/Input';
import FieldsArrayWrapper from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import DateItem from 'pages/Dashboard/pages/Billing/ClaimForm/DateItem';
import {
  ClaimDataForm,
  NotSetSentinal,
} from 'pages/Dashboard/pages/Billing/types';
import {
  useClaimSettings,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  getClaimAuthorizationOptions,
} from 'views/EMR/components/Authorizations/utils';
import {
  useGetPatientAuthorizationsByInsurance,
} from 'views/EMR/components/Authorizations/hooks';
import {
  ClaimDelayReasonCodeSettingDTO,
  ClaimResubmissionCodeSettingDTO,
} from 'dtos';
import Select from 'core/components/Select';
import USStateSelect from 'pages/Dashboard/components/USStateSelect';
import AuthorizationSelector from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector';
import {
  useAuthorizationCreation,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationCreationContext';

export default function ClaimDetails() {
  const { control, watch, setValue } = useFormContext<ClaimDataForm>();
  const { openAuthorizationCreation } = useAuthorizationCreation();
  const isAutoAccident = watch('autoAccident.isAutoAccident');
  const insuranceId = watch('insurance.insuranceId') ?? 0;
  const claimId = watch('claimId');
  const billingCodes = watch('billingCodes');
  const currentAuthorizationNumber = watch(
    'patientAuthorizationSelection',
  )?.authorizationNumber;
  const { data: patientAuthorizations = [] } = useGetPatientAuthorizationsByInsurance(insuranceId);
  const patientAuthorizationOptions = useMemo(
    () => getClaimAuthorizationOptions(
      patientAuthorizations,
      (billingCodes ?? [])
        .filter(({ serviceCodeDetails }) => serviceCodeDetails?.primaryClaimId === claimId
          || serviceCodeDetails?.secondaryClaimId === claimId)
        .map(({ code }) => code)
        .filter((code): code is string => notEmptyString(code)),
      currentAuthorizationNumber,
    ),
    [patientAuthorizations, billingCodes, currentAuthorizationNumber, claimId],
  );

  const {
    delayReasonCodeSettings,
    resubmissionCodeSettings,
  } = useClaimSettings();

  const claimDelayReasonCodeSettingsWithNotSet = [{ code: NotSetSentinal },
    ...(delayReasonCodeSettings ?? [])];

  useEffect(() => {
    if (!isAutoAccident) {
      setValue('autoAccident.autoAccidentState', null);
    }
  }, [isAutoAccident]);

  return (
    <Accordion
      defaultExpanded
      header="Details"
      disableGutters
      headerClasses={{
        root: '!px-2 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-2 populate-form gap-4 flex flex-col' }}
    >
      <Divider
        textAlign="left"
        classes={{
          wrapper: '!pl-0',
        }}
        className="populate-divider"
      >
        <h4 className="font-semibold text-xs text-gray-500">
          Is patient&lsquo;s condition related to
        </h4>
      </Divider>
      <Controller
        control={control}
        name="isRelatedToEmployment"
        render={({ field }) => (
          <Wrapper
            id="employment"
            label="Employment? (Current or Previous) (Box 10a)"
          >
            <TwoChoiceGroup
              {...field}
            />
          </Wrapper>
        )}
      />
      <div className="flex items-center w-full gap-3">
        <Controller
          control={control}
          name="autoAccident.isAutoAccident"
          render={({ field }) => (
            <Wrapper
              id="autoAccident"
              label="Auto Accident? (Box 10b)"
              className="w-40"
            >
              <TwoChoiceGroup
                {...field}
              />
            </Wrapper>
          )}
        />
        {isAutoAccident && (
          <Controller
            name="autoAccident.autoAccidentState"
            control={control}
            render={({ field }) => (
              <Wrapper
                id="state"
                label="Place (State) (Box 10b)"
                className="space-y-1 w-1/4"
              >
                <USStateSelect
                  {...field}
                  className="w-full"
                />
              </Wrapper>
            )}
          />
        )}
      </div>
      <Controller
        control={control}
        name="otherAccident.isOtherAccident"
        render={({ field }) => (
          <Wrapper
            id="otherAccident"
            label="Other Accident? (Box 10c)"
          >
            <TwoChoiceGroup
              {...field}
            />
          </Wrapper>
        )}
      />
      <Divider />
      <FieldsArrayWrapper
        formKey="dates"
        FieldComponent={DateItem}
        append={{ text: 'Add Date (Boxes 14, 15, 16, 18)' }}
        observeValues={false}
        shouldValidate={false}
      />
      <div className="flex w-full gap-3 items-center">
        <Controller
          control={control}
          name="additionalInformation"
          render={({ field }) => (
            <Input
              {...field}
              label="Additional Claim Information (Box 19)"
              className="w-full"
              inputProps={{ maxLength: 128 }}
            />
          )}
        />
      </div>
      <div className="flex lg:w-3/4 w-full gap-3 items-center">
        <Controller
          control={control}
          name="resubmissionCode"
          render={({ field }) => (
            <Select<ClaimResubmissionCodeSettingDTO>
              {...field}
              id="resubmission-code"
              className="w-[calc(50%-8px)] text-gray-500 text-xs"
              label="Resubmission Code (Box 22)"
              placeholder="Select"
              options={resubmissionCodeSettings ?? []}
              getLabel={(option) => (
                `${option?.code} - ${option?.name ?? ''}`
              )}
              getValue={(option) => option?.code ?? ''}
            />
          )}
        />
        <Controller
          control={control}
          name="originalReferenceNumber"
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              label="Original Reference Number (Box 22)"
              className="w-1/2"
            />
          )}
        />
      </div>
      <div className="flex lg:w-3/4 w-full gap-3 items-center">
        <Controller
          control={control}
          name="delayReasonCode"
          render={({ field }) => (
            <Select<ClaimDelayReasonCodeSettingDTO>
              {...field}
              id="delay-reason-code"
              className="flex-1 text-gray-500 text-xs"
              label="Delay Reason Code"
              options={claimDelayReasonCodeSettingsWithNotSet ?? []}
              getLabel={(option) =>
                (option?.code === NotSetSentinal
                  ? 'Not Set'
                  : `${option.code} - ${option.description}`)}
              getValue={(option) => option?.code ?? ''}
            />
          )}
        />
      </div>
      <div className="flex lg:w-3/4 w-full gap-3 items-center">
        <Controller
          control={control}
          name="patientAuthorizationSelection"
          render={({ field }) => (
            <AuthorizationSelector
              id="patient-authorization"
              label="Prior Auth Code (Box 23)"
              value={field.value}
              options={patientAuthorizationOptions}
              onChange={field.onChange}
              onAddAuthorization={(authorizationNumber) => {
                openAuthorizationCreation(authorizationNumber, { type: 'claim' });
              }}
              showDropdownIcon={false}
              className="w-[calc(50%-8px)]"
            />
          )}
        />
        <Controller
          control={control}
          name="referencedPrimaryPayerSecondaryIdentifier"
          render={({ field }) => (
            <Input
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              label="Other Payer Secondary Identifier"
              className="w-[calc(50%-8px)]"
            />
          )}
        />
      </div>
    </Accordion>
  );
}
